import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import {
  GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY,
  GET_TREATMENT_GROUP_MEMBERSHIPS_BY_PERSON_QUERY_KEY,
  GET_TREATMENT_GROUP_QUERY_KEY,
  GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY,
  GET_TREATMENT_GROUPS_QUERY_KEY
} from '@/constants/treatmentGroups';
import AppContext from '@/contexts/appContext';
import TreatmentGroupMembershipRepository from '@/repositories/TreatmentGroupMembershipRepository';
import { TreatmentGroupMembershipCreatePayload } from '@/types/treatmentGroup';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

// Enroll and remove both change the roster, capacity, and the requesting
// person's eligibility, so they invalidate the same keys.
const invalidateForGroup = async (
  queryClient: ReturnType<typeof useQueryClient>,
  treatmentGroupId: string
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_QUERY_KEY] }),
    queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUPS_FOR_PERSON_QUERY_KEY] }),
    queryClient.invalidateQueries({
      queryKey: [GET_TREATMENT_GROUP_MEMBERSHIPS_BY_PERSON_QUERY_KEY]
    }),
    queryClient.invalidateQueries({ queryKey: [GET_TREATMENT_GROUP_QUERY_KEY, treatmentGroupId] }),
    queryClient.invalidateQueries({
      queryKey: [GET_TREATMENT_GROUP_FOR_PERSON_QUERY_KEY, treatmentGroupId]
    })
  ]);
};

export const useEnrollTreatmentGroupMember = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupMembershipRepository = TreatmentGroupMembershipRepository();

  return useMutation({
    mutationFn: (data: TreatmentGroupMembershipCreatePayload) =>
      treatmentGroupMembershipRepository.enroll(data),
    onSuccess: async (_data, { treatment_group_id }) => {
      addMessage({ level: 'success', text: 'Member enrolled successfully' });
      await invalidateForGroup(queryClient, treatment_group_id);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useRemoveTreatmentGroupMember = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupMembershipRepository = TreatmentGroupMembershipRepository();

  return useMutation({
    mutationFn: ({ membershipId }: { membershipId: string; treatmentGroupId: string }) =>
      treatmentGroupMembershipRepository.remove(membershipId),
    onSuccess: async (_data, { treatmentGroupId }) => {
      addMessage({ level: 'success', text: 'Member removed from group successfully' });
      await invalidateForGroup(queryClient, treatmentGroupId);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

export const useUpdateTreatmentGroupMembershipSessions = () => {
  const { addMessage } = useContext(AppContext);
  const queryClient = useQueryClient();
  const treatmentGroupMembershipRepository = TreatmentGroupMembershipRepository();

  return useMutation({
    mutationFn: ({
      membershipId,
      partialSessionLetters
    }: {
      membershipId: string;
      partialSessionLetters: string[];
      treatmentGroupId: string;
    }) => treatmentGroupMembershipRepository.updateSessions(membershipId, partialSessionLetters),
    onSuccess: async (_data, { treatmentGroupId }) => {
      addMessage({ level: 'success', text: 'Session selection updated successfully' });
      await invalidateForGroup(queryClient, treatmentGroupId);
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
};

// Wraps the enroll/remove/update-sessions mutations with the handler shape
// every consumer needs: a combined pending state, and a remove that no-ops
// without a membershipId and requires the caller's own confirmation copy
// (the picker and the detail view phrase that confirmation differently --
// the detail view can name the member, the picker's list view can't).
export const useTreatmentGroupMembershipActions = () => {
  const enrollMember = useEnrollTreatmentGroupMember();
  const removeMember = useRemoveTreatmentGroupMember();
  const updateSessionsMember = useUpdateTreatmentGroupMembershipSessions();

  const enroll = (
    personId: string,
    treatmentGroupId: string,
    options?: {
      partial_session_letters?: string[];
      set_primary_provider?: boolean;
      staff_id?: string;
    }
  ) =>
    enrollMember.mutate({
      person_id: personId,
      treatment_group_id: treatmentGroupId,
      ...options
    });

  const remove = (params: {
    membershipId: string | null;
    treatmentGroupId: string;
    confirmMessage: string;
  }) => {
    const { membershipId, treatmentGroupId, confirmMessage } = params;
    if (!membershipId) return;
    if (!window.confirm(confirmMessage)) return;
    removeMember.mutate({ membershipId, treatmentGroupId });
  };

  const updateSessions = (
    membershipId: string,
    treatmentGroupId: string,
    partialSessionLetters: string[]
  ) => updateSessionsMember.mutate({ membershipId, treatmentGroupId, partialSessionLetters });

  return {
    isPending: enrollMember.isPending || removeMember.isPending || updateSessionsMember.isPending,
    enroll,
    remove,
    updateSessions
  };
};

export default useEnrollTreatmentGroupMember;
